import React, { useState } from 'react';
import HarbourButton from './harbour/HarbourButton';
import HarbourIcon from './harbour/HarbourIcon';
import HarbourNotice from './harbour/HarbourNotice';
import HarbourTextLink from './harbour/HarbourTextLink';
import HarbourTickRow from './harbour/HarbourTickRow';
import './harbour/harbour.css';

/*
 * ConfirmAndSubmitScreen (TDI-415): the one final "I have finished" moment for
 * a respondent — built once and used on the SEN pathway and the referral
 * pathway, in the service-user portal and the Harbour portals (TDI-384
 * constraint: one confirmation step, not one per pathway).
 *
 * It lists the forms the respondent completed, says plainly that answers
 * cannot be changed afterwards, and offers one action. Confirming is the
 * respondent's own act; the caller provides onConfirm (apiConfirmSubmission)
 * and decides where to go afterwards. "Come back to this later" is the honest
 * escape (text link, never a competing filled button).
 *
 * Copy here is drafted from TDI-415 and needs the copy gates (Tom: clinical
 * correctness; Jasmine: comprehension) before release — flagged on the PR.
 */
export default function ConfirmAndSubmitScreen({
  // Display titles of the forms this respondent completed, in journey order.
  formTitles = [],
  // The main action's button kind: 'family' (gold) on parent/carer and
  // service-user surfaces, 'primary' (plum) on the Harbour SENCo portal —
  // gold never appears on SENCo screens (HarbourButton's own rule).
  actionKind = 'family',
  // One pathway-specific sentence after the lock warning, supplied by the
  // caller (e.g. what confirming starts on this pathway). Optional.
  statementDetail = null,
  busy = false,
  // Statements the respondent must tick before the confirm button enables:
  // [{ key, label }]. The school's referral confirm carries the consent and
  // nominated parent-or-carer confirmations here (Tom King, 22 Oct) — the
  // referral wizard answers the nomination question, so the Request for
  // Referral no longer shows its own consent checkbox. Empty means no
  // statements (parent/carer and SEN-only confirms).
  confirmations = [],
  // A failed confirm call; the error notice says nothing was recorded.
  error = false,
  onConfirm,
  // The honest escape: either a route (string) or a click handler.
  laterTo = null,
  onLater = null,
}) {
  const count = formTitles.length;
  const [ticked, setTicked] = useState({});
  const allTicked = confirmations.every((c) => Boolean(ticked[c.key]));
  return (
    <div className="rms-confirm-submit flex flex-col gap-6" data-testid="confirm-and-submit">
      <div className="flex flex-col gap-2">
        <h2 className="harbour-heading-24">Confirm and submit</h2>
        <p className="harbour-body-16">
          {count > 0
            ? `You have finished all ${count} of your forms:`
            : 'You have finished all of your forms.'}
        </p>
        {count > 0 && (
          <ul className="flex flex-col gap-1" aria-label="Your completed forms">
            {formTitles.map((title) => (
              <li key={title} className="harbour-body-16 flex items-center gap-2">
                <HarbourIcon name="check" size={16} />
                <span>{title}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <p className="harbour-body-16-bold">
          When you confirm, your answers are submitted. After that you cannot change them.
        </p>
        {statementDetail && <p className="harbour-body-16">{statementDetail}</p>}
      </div>

      {confirmations.length > 0 && (
        <div className="flex flex-col gap-2" role="group" aria-label="Confirm these statements">
          {confirmations.map((c) => (
            <HarbourTickRow
              key={c.key}
              label={c.label}
              ticked={Boolean(ticked[c.key])}
              disabled={busy}
              onToggle={(next) => setTicked((t) => ({ ...t, [c.key]: next }))}
            />
          ))}
        </div>
      )}

      {error && (
        <HarbourNotice kind="error" title="We could not confirm your forms">
          Check your internet connection and try again. Nothing has been submitted yet.
        </HarbourNotice>
      )}

      <div className="flex flex-col items-start gap-4">
        <HarbourButton
          kind={actionKind}
          busy={busy}
          disabled={busy || !allTicked}
          aria-busy={busy}
          onClick={onConfirm}
        >
          Confirm and submit
        </HarbourButton>
        {(laterTo || onLater) && (
          <HarbourTextLink to={laterTo || undefined} onClick={onLater || undefined}>
            Come back to this later
          </HarbourTextLink>
        )}
      </div>
    </div>
  );
}
